// react
import { ReactNode, useContext } from 'react';

// types
import { Option } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// context
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';
import { ContextUser } from '../../../App.context';

// mui
import { SelectChangeEvent } from '@mui/material';

// fields
import { Select } from '../Select/Select';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface TransactionPageOption {
	label: string;
	permission?: boolean;
	to: string;
	value: string;
}

export interface SelectTransactionPageProps {
	onChange?: (value: string) => void;
	value: string;
}

export const SelectTransactionPage = ({ onChange, value }: SelectTransactionPageProps) => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// options
	const transactionPages: TransactionPageOption[] = [
		{
			label: t('advances'),
			permission: true,
			to: `/project/${project?.idWbg}/transactions/advances`,
			value: 'advances',
		},
		{
			label: t('cashBenefits'),
			permission: userProjectPermissions?.cashBenefit?.read,
			to: `/project/${project?.idWbg}/transactions/cash-benefits`,
			value: 'cashBenefits',
		},
		{
			label: t('historicalTransactions'),
			permission: userProjectPermissions?.document?.read,
			to: `/project/${project?.idWbg}/transactions/historical`,
			value: 'historical',
		},
		{
			label: t('inflows'),
			permission: userProjectPermissions?.replenishment?.read,
			to: `/project/${project?.idWbg}/transactions/inflows`,
			value: 'inflows',
		},
		{
			label: t('nonCashBenefits'),
			permission: userProjectPermissions?.nonCashBenefit?.read,
			to: `/project/${project?.idWbg}/transactions/non-cash-benefits`,
			value: 'nonCashBenefits',
		},
		{
			label: t('operatingExpenses'),
			permission: userProjectPermissions?.opEx?.read,
			to: `/project/${project?.idWbg}/transactions/opex`,
			value: 'opex',
		},
		{
			label: t('payments'),
			permission: userProjectPermissions?.payment?.read,
			to: `/project/${project?.idWbg}/transactions/payments`,
			value: 'payments',
		},
		{
			label: t('paymentRefunds'),
			permission: userProjectPermissions?.payment?.read,
			to: `/project/${project?.idWbg}/transactions/payment-refunds`,
			value: 'paymentRefunds',
		},
		{
			label: t('specialCommitments'),
			permission: featureFlags['SPECIAL'] && userProjectPermissions?.payment?.read,
			to: `/project/${project?.idWbg}/transactions/special`,
			value: 'special',
		},
		{
			label: t('reimbursements'),
			permission:
				featureFlags['REIMBURSEMENT'] &&
				(user?.isWbg || userProjectPermissions?.reimbursement?.read),
			to: `/project/${project?.idWbg}/transactions/reimbursements`,
			value: 'reimbursements',
		},
		{
			label: t('transfers'),
			permission: userProjectPermissions?.transfer?.read,
			to: `/project/${project?.idWbg}/transactions/transfers`,
			value: 'transfers',
		},
	];

	// filter options based on permissions and convert to Option format
	const options: Option[] = transactionPages
		.filter((page) => page.permission === undefined || page.permission === true)
		.map((page) => ({
			label: page.label,
			value: page.value,
		}));

	const handleChange = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		const newValue = e.target.value as string;
		const selectedPage = transactionPages.find((page) => page.value === newValue);

		if (selectedPage) {
			navigate(selectedPage.to);
		}

		if (onChange) {
			onChange(newValue);
		}
	};

	return (
		<Select
			data-tour="selectTransactionPage"
			hasNoneOption={false}
			id="select-transaction-page"
			onChange={handleChange}
			options={options}
			required
			value={value}
		/>
	);
};
